import React, { useEffect, useMemo, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';

import { SendoutInterviewDetailsTabs } from './SendoutInterviewDetailsTabs';
import { INTERVIEW_DRAWER_MODES, TAB_IDS } from './SendoutInterviewDrawer.constants';
import { INTERVIEW_TAB_CONFIG } from './SendoutInterviewDrawer.utils';
import { useInterviewTabData } from './useInterviewTabData';

const CLOSE_FORM_STRINGS = strings.sendoutsV2.alerts.confirmationDialogs.closeForm;
const INTERVIEW_DOCUMENT_TYPE = CLOSE_FORM_STRINGS.documentTypes.interview;

export const SendoutInterviewDetailsDrawer = ({
  interviewId,
  isSubmitting = false,
  mode = INTERVIEW_DRAWER_MODES.View,
  onClose = () => {},
  onSaved = () => {},
  sendoutId
}) => {
  const isEditMode = mode === INTERVIEW_DRAWER_MODES.Edit;
  const [selectedTab, setSelectedTab] = useState(TAB_IDS.InterviewDetails);
  const [isFormPreloaded, setIsFormPreloaded] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const form = useForm();
  const { clearError, handleSubmit, reset, setError } = form;

  const tab = INTERVIEW_TAB_CONFIG[selectedTab];
  const { state } = useInterviewTabData({
    endpoint: tab.buildEndpoint({ sendoutId, interviewId })
  });

  const isFetching = state.status === UIStatus.Loading;
  const hasLoadError = state.status === UIStatus.Error;
  const record = state.results;
  const isRecordMissing = state.status === UIStatus.Success && !record;
  const values = useMemo(() => (record ? tab.buildDefaultValues(record) : null), [record, tab]);

  useEffect(() => {
    reset(values ?? {});
    setIsFormPreloaded(!!values);
  }, [reset, values]);

  const canUpdate =
    isEditMode && !isFetching && !hasLoadError && (isFormPreloaded || isRecordMissing);

  const handleSave = async formData => {
    if (tab.validate && !tab.validate({ clearError, formData, setError })) return;

    await onSaved(tab.buildSubmitArgs(formData, { existingItemId: record?.id ?? null }));
  };

  const handleClose = () => {
    if (isSubmitting) return;

    if (canUpdate) {
      setIsCloseDialogOpen(true);
      return;
    }

    onClose();
  };

  const handleConfirmDrawerClose = () => {
    setIsCloseDialogOpen(false);
    onClose();
  };

  const CustomCloseDialogActions = ({ defaultButtonsProps, ActionsMapper }) => {
    const { onClose: closeDialog } = defaultButtonsProps;

    return (
      <ActionsMapper
        buttons={[
          {
            variant: 'outlined',
            text: CLOSE_FORM_STRINGS.actions.discardAndExit,
            onClick: handleConfirmDrawerClose
          },
          {
            text: strings.formatString(CLOSE_FORM_STRINGS.actions.cancel, {
              documentType: INTERVIEW_DOCUMENT_TYPE
            }),
            onClick: closeDialog
          }
        ]}
      />
    );
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: handleClose,
        disabled: isSubmitting
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.update,
        variant: 'contained',
        disabled: !canUpdate || isSubmitting,
        onClick: handleSubmit(handleSave)
      },
      GridItemProps: { md: 3 }
    }
  ];

  const handleChangeTabIndex = (_event, tabId) => setSelectedTab(tabId);

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{ title: strings.sendoutsV2.drawers.sendoutInterviewDetails.title }}
        footer={
          isEditMode ? (
            <FPButtonBuilder
              GridContainerProps={{ spacing: 2 }}
              buttonDefinitions={buttonDefinitions}
            />
          ) : undefined
        }
        onClose={handleClose}
        hasPaddingContent={false}
      >
        <FormContext {...form}>
          <SendoutInterviewDetailsTabs
            hasLoadError={hasLoadError}
            isEditMode={isEditMode}
            isFetching={isFetching}
            isFormPreloaded={isFormPreloaded}
            isRecordMissing={isRecordMissing}
            onChangeTabIndex={handleChangeTabIndex}
            record={record}
            selectedTab={selectedTab}
          />
        </FormContext>
      </FPDrawer>

      <When condition={isCloseDialogOpen}>
        <DecisionDialog
          CustomActions={CustomCloseDialogActions}
          isOpened
          message={strings.formatString(CLOSE_FORM_STRINGS.message, {
            documentType: INTERVIEW_DOCUMENT_TYPE
          })}
          onClose={() => setIsCloseDialogOpen(false)}
          onConfirm={handleConfirmDrawerClose}
          showSeverityIcon
          title={strings.formatString(CLOSE_FORM_STRINGS.title, {
            documentType: INTERVIEW_DOCUMENT_TYPE
          })}
        />
      </When>
    </>
  );
};
